import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import Utils, { GetHelper, payloadsEqual } from 'src/app/helpers/utils';
import { ApiService } from 'src/app/services/api/api.service';
import { SignupService } from 'src/app/services/signup/signup.service';
import { ToastService } from 'src/app/services/toast/toast.service';

export type CompanyIncomeSalaryValues = {
  salary: string;
  other_income: string;
  id: number;
  financial_position_at_pj: string;
};

@Component({
  selector: 'app-signup-unified-company-income',
  templateUrl: './signup-unified-company-income.component.html',
  styleUrls: ['./signup-unified-company-income.component.scss'],
})
export class SignupUnifiedCompanyIncomeComponent implements OnInit, OnDestroy {
  form = this.formBuilder.group({
    wage: [0, [Validators.min(1)]],
    other_income: [0, [Validators.min(1)]],
    financial_position_at_pj: [
      null,
      [
        Utils.validateWhenNotEmpty(Utils.validateMaxMinToday),
        Utils.validateWhenNotEmpty(Utils.validateMinDate),
      ],
    ],
  });

  event: any;
  data: CompanyIncomeSalaryValues;

  /** Snapshot do payload após o GET; usado para evitar PATCH/PUT quando nada mudou. */
  private initialPayload: Record<string, unknown> | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private signupService: SignupService,
    private toast: ToastService,
    private api: ApiService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  ngOnDestroy(): void {
    this.event.unsubscribe();
  }

  ngOnInit(): void {
    this.event = this.signupService.sendCompanyData.subscribe((value) => {
      if (value === 'income') {
        this.sendData();
      }
    });

    this.getData();

    if (this.router.url.includes('pj')) {
      this.form.controls.wage.setValidators([]);
      this.form.controls.wage.updateValueAndValidity();
      this.form.controls.other_income.setValidators([]);
      this.form.controls.other_income.updateValueAndValidity();
    }

    this.signupService.setCompanyFormStatus('incomeSalary', this.form.valid);

    this.form.statusChanges.subscribe((status) => {
      this.signupService.setCompanyFormStatus(
        'incomeSalary',
        status === 'VALID',
      );
    });
  }

  async getData() {
    try {
      // Sempre pegar o UUID da URL
      const routeId = this.route.snapshot.paramMap.get('id');

      if (!routeId) {
        console.error('Register UUID não encontrado na URL');
        return;
      }

      const { data } = await this.api.get({
        route: 'api/registration/wage/',
        params: { register_uuid: routeId },
        token: true,
      });

      this.form.patchValue({
        ...data,
        financial_position_at_pj: data.financial_position_at_pj
          ? data.financial_position_at_pj.split('-').reverse().join('/')
          : null,
      });

      this.data = data;
      this.initialPayload = this.buildPayloadFromForm();
    } catch (error) {
      this.initialPayload = null;
    }
  }

  private buildPayloadFromForm(): Record<string, unknown> {
    const valuesHelper = new GetHelper(this.form.value);
    const fp = valuesHelper.get('financial_position_at_pj');
    return {
      wage: valuesHelper.get('wage'),
      other_income: valuesHelper.get('other_income'),
      financial_position_at_pj: fp && typeof fp === 'string'
        ? fp.split('/').reverse().join('-')
        : null,
    };
  }

  async sendData() {
    const routeId = this.route.snapshot.paramMap.get('id');

    if (!routeId) {
      this.toast.show(
        'error',
        'Erro',
        'UUID do registro não encontrado na URL',
      );
      return;
    }

    const payload = this.buildPayloadFromForm();

    if (payloadsEqual(payload as Record<string, any>, this.initialPayload ?? undefined)) {
      this.signupService.loading = false;
      await this.signupService.updateUserStepCompany(
        'register',
        'declarations',
        routeId,
      );
      return;
    }

    this.signupService.loading = true;

    try {
      if (this.data && this.data.id) {
        const { data } = await this.api.put({
          route: `api/registration/wage/${this.data.id}/`,
          params: { register_uuid: routeId },
          token: true,
          body: payload,
        });

        this.data = data;
      } else {
        await this.api.post({
          route: 'api/registration/wage/',
          params: { register_uuid: routeId },
          token: true,
          body: payload,
        });
      }

      this.initialPayload = this.buildPayloadFromForm();
      this.toast.show('info', 'Sucesso', 'Dados salvos com sucesso!');
    } catch (error) {
      this.toast.show(
        'error',
        'Erro',
        error?.error?.message || 'Erro ao salvar os dados',
      );
      this.signupService.loading = false;
    } finally {
      this.signupService.loading = false;
    }
  }
}
